import React, { useCallback } from 'react';

import { View } from 'react-native';

import { IBaseProps } from '@rbi-ctg/frontend';
import { useMatch } from 'state/location';
import { CustomEventNames, useMParticleContext } from 'state/mParticle';

import { NAV_X_PADDING } from './constants';
import StyledLink from './styled-link';

interface INavLinkProps extends IBaseProps {
  to: string;
  hidden?: boolean;
  requiresExactMatch?: boolean;
  onClick?: (to?: string, isActive?: boolean) => void;
  component?(isActive: boolean): React.ReactNode;
  navBarText?: string;
  componentKey?: string;
  isFirst: boolean;
  isLast: boolean;
}

const MemoizedNavLink = React.memo(function MemoizedNavLink({
  to,
  isActive,
  component,
  children,
  testID: dataQaId,
  onClick,
  hidden,
  componentKey,
  navBarText = '',
  isFirst,
  isLast,
}: INavLinkProps & {
  isActive: boolean;
}) {
  const mParticle = useMParticleContext();

  const onClickAndLogToMParticle = useCallback(() => {
    if (navBarText !== '') {
      mParticle.logNavBarClickEvent(navBarText, componentKey);
    }
    mParticle.logNavigationClick(CustomEventNames.NAV_CLICK, { linkTo: to });

    if (onClick) {
      onClick(to, isActive);
    }
  }, [componentKey, navBarText, mParticle, to, onClick, isActive]);
  return (
    <StyledLink
      touchableOpacityEffect
      to={to}
      onPress={onClickAndLogToMParticle}
      testID={dataQaId}
      hitSlop={{
        top: 10,
        right: isFirst ? NAV_X_PADDING : undefined,
        left: isLast ? NAV_X_PADDING : undefined,
        bottom: 10,
      }}
    >
      <View focusable={hidden ? false : undefined}>
        {component ? component(isActive) : children}
      </View>
    </StyledLink>
  );
});

export const NavLink = ({ requiresExactMatch, to, ...restProps }: INavLinkProps) => {
  const pathToMatch = requiresExactMatch ? to : `${to}/*`;
  const isActive = !!useMatch(pathToMatch);
  return <MemoizedNavLink isActive={isActive} to={to} {...restProps} />;
};

export const iconSize = 25;
